package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.domain.model.ClassProgressState
import com.elan.edubridge.shared.domain.model.TodayClass
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import org.w3c.dom.HTMLInputElement

/**
 * 수업 목록(가이드 §3.4). 상태칩 3종은 C4 해소안을 그대로 따른다 — 판정 자체는
 * [TodayClass.state]로 이미 서버/데이터 레이어에서 확정되어 오므로, 여기서는 상태→칩 표시만 매핑한다.
 */
object TodayClassesPanel {
    fun render(academyId: String, classes: List<TodayClass>): String {
        if (classes.isEmpty()) {
            return """
                <section class="eb-card eb-class-list" aria-labelledby="class-list-title">
                    <h2 id="class-list-title">수업 목록</h2>
                    <div class="eb-empty-state">
                        ${icon("icon-calendar", "xl")}
                        <h2>오늘은 예정된 수업이 없어요</h2>
                        <p>반을 만들고 요일·시간을 배정하면 여기에 나타나요.</p>
                    </div>
                </section>
            """.trimIndent()
        }

        val rows = classes.joinToString("") { todayClass ->
            """
            <tr data-class-id="${todayClass.classId}">
                <td><input type="checkbox" class="eb-class-row-check" value="${todayClass.classId.escapeHtml()}" aria-label="${todayClass.className.escapeHtml()} 선택"></td>
                <td>${todayClass.className.escapeHtml()}</td>
                <td><time>${todayClass.schedule.startLabel}–${todayClass.schedule.endLabel}</time></td>
                <td>${todayClass.teacher.escapeHtml()}</td>
                <td>${stateChip(todayClass.state)}</td>
                <td><a class="eb-table__link" href="/app/$academyId/students">명단 ${todayClass.checkedInCount}/${todayClass.enrolledCount}</a></td>
            </tr>
            """.trimIndent()
        }

        return """
            <section class="eb-card eb-class-list" aria-labelledby="class-list-title">
                <h2 id="class-list-title">수업 목록</h2>
                <div class="eb-table-scroll">
                    <table class="eb-table">
                        <thead>
                            <tr>
                                <th scope="col"><input type="checkbox" id="class-select-all" aria-label="전체 수업 선택"></th>
                                <th scope="col">수업명</th><th scope="col">시간</th><th scope="col">선생님</th><th scope="col">상태</th>
                                <th scope="col"><span class="eb-sr-only">명단</span></th>
                            </tr>
                        </thead>
                        <tbody id="class-rows">$rows</tbody>
                    </table>
                </div>
                <div class="eb-today-actions">
                    <button class="eb-button eb-button--primary" id="mark-attendance">출석 처리</button>
                    <a class="eb-button eb-button--link" href="/app/$academyId/stats">전체 기록</a>
                </div>
            </section>
        """.trimIndent()
    }

    /** 체크된 수업 id 목록. "출석 처리" 클릭 시점에 DOM에서 직접 읽는다(별도 상태 보관 없음). */
    fun selectedClassIds(): List<String> {
        val boxes = document.querySelectorAll(".eb-class-row-check")
        val result = mutableListOf<String>()
        for (index in 0 until boxes.length) {
            val box = boxes.item(index) as? HTMLInputElement ?: continue
            if (box.checked) result += box.value
        }
        return result
    }

    fun wireSelectAll() {
        val selectAll = document.getElementById("class-select-all") as? HTMLInputElement ?: return
        selectAll.onchange = {
            val boxes = document.querySelectorAll(".eb-class-row-check")
            for (index in 0 until boxes.length) {
                (boxes.item(index) as? HTMLInputElement)?.checked = selectAll.checked
            }
            null
        }
    }

    /** 3-상태 칩 + DONE(칩 없음, 옅은 회색 텍스트) — 가이드 §3.4 C4 해소안. */
    private fun stateChip(state: ClassProgressState): String = when (state) {
        ClassProgressState.SCHEDULED -> """<span class="eb-chip eb-chip--info">${icon("icon-dot", "xs")}${state.displayName}</span>"""
        ClassProgressState.IN_PROGRESS -> """<span class="eb-chip eb-chip--success eb-chip--success--icon">${icon("icon-dot", "xs")}${state.displayName}</span>"""
        ClassProgressState.NEEDS_REVIEW -> """<span class="eb-chip eb-chip--warning">${icon("icon-warning", "xs")}${state.displayName}</span>"""
        ClassProgressState.DONE -> """<span class="eb-meta">${state.displayName}</span>"""
    }
}
